import React, { useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Box,
  Container,
  Badge,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery,
  Slide,
  useScrollTrigger,
  Chip,
  Avatar,
  Alert,
  Snackbar,
  Tooltip,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Home,
  Inventory,
  Category,
  ContactPhone,
  Info,
  Phone,
  WhatsApp,
  Favorite,
  Search,
  KeyboardArrowDown,
  Login as LoginIcon,
  AccountCircle,
  Logout as LogoutIcon,
  AdminPanelSettings,
  PersonAdd,
  RequestQuote,
  Inventory2,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import mockProducts from "../../mockData/mockProducts"; // Asegúrate de que la ruta sea correcta
import LoginForm from "../forms/LoginForm";
import RegisterForm from "../forms/RegisterForm";
import VerifyCodeForm from "../forms/VerifyCodeForm";
import { useApp } from "../../hooks/useApp";
import SimpleLanguageToggle from "./SimpleLanguageToggle";
import { useAuth, useFavorites } from "../../context/context";
import tattersallLogo from "../../assets/TT_VENTA_USADOS.png";
import useNavigationMemory from "../../hooks/useNavigationMemory";
import NotificationBanner from "./NotificationBanner";
import usePendingChangesCount from "../../hooks/usePendingChangesCount";

// Logo component
const Logo = ({ size = "medium", color = "white" }) => {
  const logoSizes = {
    small: { height: 23, fontSize: "1.2rem" },
    medium: { height: 40, fontSize: "1.5rem" },
    large: { height: 50, fontSize: "1.8rem" },
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box
        component="img"
        src={tattersallLogo}
        alt="Tattersall Logo"
        title="Tattersall Remates"
        sx={{
          height: logoSizes[size].height,
          width: "auto",
          objectFit: "contain",
        }}
        onError={(e) => {
          // Fallback si no encuentra la imagen
          console.error("Error cargando logo:", e);
          e.target.style.display = "none";
        }}
      />
    </Box>
  );
};

// Scroll behavior component
function HideOnScroll({ children }) {
  const trigger = useScrollTrigger();
  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

// Desktop Navbar
const DesktopNavbar = ({ props }) => {
  const { setLoginOpen } = props;
  const { getFavoritesCount } = useFavorites(); // 🔧 Cambiar al nuevo hook
  const { user, logout, isAdmin } = useAuth();
  const isBienesAdmin = !!user && isAdmin?.() && user?.areaId === 1;
  const { count: pendingCount } = usePendingChangesCount({
    enabled: isBienesAdmin,
  });
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);

  const {
    navigate,
    categoriesMenuAnchor,
    scrolled,
    categories,
    menuItems,
    handleMenuClick,
    handleCategoriesClick,
    handleCategoriesClose,
    handleCategorySelect,
    isActiveRoute,
  } = useNavbar();

  const handleUserMenuClick = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = () => {
    logout();
    handleUserMenuClose();
    navigate("/");
  };

  const handleAdminAccess = () => {
    navigate("/admin");
    handleUserMenuClose();
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        background: "linear-gradient(135deg, #004b93 0%, #003366 100%)",
        // opacity: 0.95,
        backdropFilter: scrolled ? "blur(10px)" : "none",
        transition: "all 0.3s ease",
        boxShadow: scrolled ? "0 4px 20px rgba(0,0,0,0.15)" : "none",
        margin: 0,
      }}
    >
      <Container maxWidth="xl" id="navbar-container">
        <Toolbar sx={{ justifyContent: "space-between", py: 0 }}>
          {/* Logo */}
          <Box sx={{ cursor: "pointer" }} onClick={() => navigate("/")}>
            <Logo size={"large"} />
          </Box>

          {/* Navigation Menu */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {menuItems.map((item) => (
              <Box key={item.label}>
                {item.hasSubmenu ? (
                  <>
                    <Button
                      color="inherit"
                      onClick={handleCategoriesClick}
                      endIcon={<KeyboardArrowDown />}
                      sx={{
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 500,
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.1)",
                        },
                      }}
                    >
                      {item.label}
                    </Button>
                    <Menu
                      anchorEl={categoriesMenuAnchor}
                      open={Boolean(categoriesMenuAnchor)}
                      onClose={handleCategoriesClose}
                      disableScrollLock
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                      }}
                      slotProps={{
                        paper: {
                          sx: {
                            mt: 1,
                            minWidth: 200,
                            maxHeight: 400,
                            "& .MuiMenuItem-root": {
                              px: 2,
                              py: 1,
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem
                        onClick={() => handleCategorySelect("")}
                        sx={{ fontWeight: "bold" }}
                      >
                        Todas las Categorías
                      </MenuItem>
                      <Divider />
                      {categories.map((category) => (
                        <MenuItem
                          key={category}
                          onClick={() => handleCategorySelect(category)}
                        >
                          {category}
                          <Chip
                            size="small"
                            label={
                              mockProducts.filter(
                                (p) => p.category === category,
                              ).length
                            }
                            sx={{ ml: "auto", fontSize: "0.7rem" }}
                          />
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                ) : (
                  <Button
                    color="inherit"
                    onClick={() => handleMenuClick(item.path)}
                    sx={{
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 500,
                      backgroundColor: isActiveRoute(item.path)
                        ? "rgba(255,255,255,0.15)"
                        : "transparent",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.1)",
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                )}
              </Box>
            ))}
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* <IconButton
              color="inherit"
              onClick={() => setSearchOverlayOpen(true)}
              sx={{
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              <Search />
            </IconButton> */}
            <Tooltip title="Mis Favoritos">
              <IconButton
                color="inherit"
                onClick={() => {
                  if (!user) {
                    setLoginOpen(true);
                    return;
                  }
                  navigate("/favoritos");
                }}
                sx={{
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                <Badge badgeContent={getFavoritesCount()} color="error">
                  <Favorite />
                </Badge>
              </IconButton>
            </Tooltip>
            {/* Icono de Cotizaciones */}
            <Tooltip title="Mis Cotizaciones">
              <IconButton
                color="inherit"
                onClick={() => {
                  if (!user) {
                    setLoginOpen(true);
                    return;
                  }
                  navigate("/cotizaciones");
                }}
                sx={{
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                <RequestQuote />
              </IconButton>
            </Tooltip>
            {/* Acceso directo al Backoffice (solo admin BIENES) */}
            {isBienesAdmin && (
              <Tooltip
                title={
                  pendingCount > 0
                    ? `Backoffice • ${pendingCount} pendiente${pendingCount === 1 ? "" : "s"} de aprobar`
                    : "Backoffice"
                }
              >
                <IconButton
                  onClick={() => navigate("/admin")}
                  sx={{
                    ml: 0.5,
                    color: "#1a1a1a",
                    backgroundColor: "#FFC107",
                    border: "1.5px solid rgba(255,255,255,0.6)",
                    boxShadow: "0 0 0 2px rgba(255, 193, 7, 0.25)",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "#FFD54F",
                      transform: "scale(1.05)",
                      boxShadow: "0 0 0 3px rgba(255, 193, 7, 0.4)",
                    },
                  }}
                >
                  <Badge badgeContent={pendingCount} color="error" max={99}>
                    <AdminPanelSettings />
                  </Badge>
                </IconButton>
              </Tooltip>
            )}
            {/* Login/User Menu */}
            {user ? (
              <>
                <Button
                  color="inherit"
                  onClick={handleUserMenuClick}
                  startIcon={
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        bgcolor: "rgba(255,255,255,0.2)",
                      }}
                    >
                      <Typography>
                        {user.name.charAt(0).toUpperCase()}
                      </Typography>
                    </Avatar>
                  }
                  endIcon={<KeyboardArrowDown />}
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 500,
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  {user.name}
                </Button>
                <Menu
                  anchorEl={userMenuAnchor}
                  open={Boolean(userMenuAnchor)}
                  onClose={handleUserMenuClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  disableScrollLock
                >
                  <MenuItem disabled>
                    <Box>
                      <Typography variant="subtitle2">{user.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {user.email}
                      </Typography>
                    </Box>
                  </MenuItem>
                  <Divider />
                  <MenuItem
                    onClick={() => {
                      navigate("/favoritos");
                      handleUserMenuClose();
                    }}
                  >
                    <ListItemIcon>
                      <Favorite fontSize="small" />
                    </ListItemIcon>
                    Mis Favoritos
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      navigate("/cotizaciones");
                      handleUserMenuClose();
                    }}
                  >
                    <ListItemIcon>
                      <RequestQuote fontSize="small" />
                    </ListItemIcon>
                    Mis Cotizaciones
                  </MenuItem>
                  <Divider />
                  {isAdmin() && (
                    <MenuItem onClick={handleAdminAccess}>
                      <ListItemIcon>
                        <AdminPanelSettings fontSize="small" />
                      </ListItemIcon>
                      Administración
                    </MenuItem>
                  )}
                  <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                      <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    Cerrar Sesión
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Button
                variant="outlined"
                startIcon={<LoginIcon />}
                onClick={() => setLoginOpen(true)}
                sx={{
                  borderColor: "white",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    borderColor: "white",
                  },
                  ml: 1,
                }}
              >
                Iniciar Sesión
              </Button>
            )}
            {/* Botón de idioma simple */}
            {/* <SimpleLanguageToggle color="inherit" /> */}
            {/* <Button
              variant="contained"
              startIcon={<Phone />}
              href="tel:+56993015600"
              sx={{
                backgroundColor: "white",
                color: "primary.main",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
                ml: 1,
              }}
            >
              Llamar
            </Button>

            <Button
              variant="outlined"
              startIcon={<WhatsApp />}
              href="https://wa.me/56993015600"
              target="_blank"
              sx={{
                borderColor: "white",
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.1)",
                  borderColor: "white",
                },
              }}
            >
              WhatsApp
            </Button> */}
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

// Mobile Navbar
const MobileNavbar = ({ props }) => {
  const { setLoginOpen } = props;
  const { user, logout, isAdmin } = useAuth();
  const isBienesAdmin = !!user && isAdmin?.() && user?.areaId === 1;
  const { count: pendingCount } = usePendingChangesCount({
    enabled: isBienesAdmin,
  });
  const {
    scrolled,
    mobileMenuOpen,
    setMobileMenuOpen,
    navigate,
    menuItems,
    handleMenuClick,
    isActiveRoute,
    categories,
    handleCategorySelect,
  } = useNavbar();
  const { getFavoritesCount } = useFavorites(); // 🔧 Cambiar al nuevo hook
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);

  const handleUserMenuClick = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleAdminAccess = () => {
    navigate("/admin");
    handleUserMenuClose();
  };

  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
    navigate("/");
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          background: "linear-gradient(135deg, #004b93 0%, #003366 100%)",
          backdropFilter: scrolled ? "blur(10px)" : "none",
          transition: "all 0.3s ease",
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          {/* Mobile Logo */}
          <Box sx={{ cursor: "pointer" }} onClick={() => navigate("/")}>
            <Logo size="small" />
          </Box>

          {/* Action Icons */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* Favoritos */}
            <IconButton
              color="inherit"
              onClick={() => {
                if (!user) {
                  setLoginOpen(true);
                  setMobileMenuOpen(false);
                  return;
                }
                navigate("/favoritos");
                setMobileMenuOpen(false);
              }}
            >
              <Badge badgeContent={getFavoritesCount()} color="error">
                <Favorite />
              </Badge>
            </IconButton>

            {/* Cotizaciones */}
            <IconButton
              color="inherit"
              onClick={() => {
                if (!user) {
                  setLoginOpen(true);
                  setMobileMenuOpen(false);
                  return;
                }
                navigate("/cotizaciones");
                setMobileMenuOpen(false);
              }}
            >
              <RequestQuote />
            </IconButton>

            {/* Backoffice (solo admin BIENES) */}
            {isBienesAdmin && (
              <IconButton
                onClick={() => {
                  navigate("/admin");
                  setMobileMenuOpen(false);
                }}
                sx={{
                  ml: 0.5,
                  color: "#1a1a1a",
                  backgroundColor: "#FFC107",
                  border: "1.5px solid rgba(255,255,255,0.6)",
                  boxShadow: "0 0 0 2px rgba(255, 193, 7, 0.25)",
                  "&:hover": { backgroundColor: "#FFD54F" },
                }}
              >
                <Badge badgeContent={pendingCount} color="error" max={99}>
                  <AdminPanelSettings />
                </Badge>
              </IconButton>
            )}

            {/* Login button for mobile */}
            {user ? (
              <>
                <>
                  <IconButton color="inherit" onClick={handleUserMenuClick}>
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        bgcolor: "rgba(255,255,255,0.2)",
                      }}
                    >
                      <Typography>
                        {user.name.charAt(0).toUpperCase()}
                      </Typography>
                    </Avatar>
                  </IconButton>
                  <Menu
                    anchorEl={userMenuAnchor}
                    open={Boolean(userMenuAnchor)}
                    onClose={handleUserMenuClose}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    disableScrollLock
                  >
                    <MenuItem disabled>
                      <Box>
                        <Typography variant="subtitle2">{user.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.email}
                        </Typography>
                      </Box>
                    </MenuItem>
                    <Divider />
                    <MenuItem
                      onClick={() => {
                        navigate("/favoritos");
                        handleUserMenuClose();
                      }}
                    >
                      <ListItemIcon>
                        <Favorite fontSize="small" />
                      </ListItemIcon>
                      Mis Favoritos
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        navigate("/cotizaciones");
                        handleUserMenuClose();
                      }}
                    >
                      <ListItemIcon>
                        <RequestQuote fontSize="small" />
                      </ListItemIcon>
                      Mis Cotizaciones
                    </MenuItem>
                    <Divider />
                    {isAdmin() && (
                      <MenuItem onClick={handleAdminAccess}>
                        <ListItemIcon>
                          <AdminPanelSettings fontSize="small" />
                        </ListItemIcon>
                        Administración
                      </MenuItem>
                    )}
                    <MenuItem onClick={handleLogout}>
                      <ListItemIcon>
                        <LogoutIcon fontSize="small" />
                      </ListItemIcon>
                      Cerrar Sesión
                    </MenuItem>
                  </Menu>
                </>
              </>
            ) : (
              <IconButton color="inherit" onClick={() => setLoginOpen(true)}>
                <LoginIcon />
              </IconButton>
            )}

            <IconButton color="inherit" onClick={() => setMobileMenuOpen(true)}>
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        PaperProps={{
          sx: {
            width: "100%",
            maxWidth: 400,
            background: "linear-gradient(135deg, #004b93 0%, #003366 100%)",
            color: "white",
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Header */}

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            {/* <SimpleLanguageToggle color="inherit" /> */}

            <Logo size="medium" />
            <IconButton
              color="inherit"
              onClick={() => setMobileMenuOpen(false)}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Navigation Menu */}
          <List>
            {menuItems.map((item) => (
              <Box key={item.label}>
                <ListItem
                  onClick={() =>
                    item.hasSubmenu ? null : handleMenuClick(item.path)
                  }
                  sx={{
                    borderRadius: 2,
                    mb: 1,
                    backgroundColor: isActiveRoute(item.path)
                      ? "rgba(255,255,255,0.15)"
                      : "transparent",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: "white", minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    slotProps={{
                      primary: {
                        fontWeight: isActiveRoute(item.path)
                          ? "bold"
                          : "normal",
                      },
                    }}
                  />
                </ListItem>

                {/* Submenu for categories */}
                {item.hasSubmenu && (
                  <Box sx={{ pl: 6, pr: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1, opacity: 0.8 }}>
                      Categorías disponibles:
                    </Typography>
                    {categories.slice(0, 5).map((category) => (
                      <Button
                        key={category}
                        fullWidth
                        variant="text"
                        onClick={() => handleCategorySelect(category)}
                        sx={{
                          justifyContent: "flex-start",
                          color: "white",
                          textTransform: "none",
                          mb: 0.5,
                          "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.1)",
                          },
                        }}
                      >
                        {category}
                      </Button>
                    ))}
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => handleCategorySelect("")}
                      sx={{
                        borderColor: "white",
                        color: "white",
                        textTransform: "none",
                        mt: 1,
                      }}
                    >
                      Ver todas las categorías
                    </Button>
                  </Box>
                )}
              </Box>
            ))}
          </List>

          <Divider sx={{ borderColor: "rgba(255,255,255,0.2)", mb: 2 }} />

          {/* User Info */}
          {user ? (
            <Box
              sx={{
                mb: 3,
                p: 2,
                bgcolor: "rgba(255,255,255,0.1)",
                borderRadius: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: "rgba(255,255,255,0.2)",
                    mr: 2,
                  }}
                >
                  {user.name.charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {user.name}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    {user.email}
                  </Typography>
                </Box>
              </Box>

              {/* Botones de navegación del usuario */}
              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  startIcon={<Favorite />}
                  onClick={() => {
                    navigate("/favoritos");
                    setMobileMenuOpen(false);
                  }}
                  sx={{
                    borderColor: "white",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  Favoritos
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  startIcon={<RequestQuote />}
                  onClick={() => {
                    navigate("/cotizaciones");
                    setMobileMenuOpen(false);
                  }}
                  sx={{
                    borderColor: "white",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  Cotizaciones
                </Button>
              </Box>

              <Box sx={{ display: "flex", gap: 1 }}>
                {isAdmin() && (
                  <Button
                    variant="outlined"
                    size="small"
                    fullWidth
                    startIcon={<AdminPanelSettings />}
                    onClick={() => {
                      navigate("/admin");
                      setMobileMenuOpen(false);
                    }}
                    sx={{
                      borderColor: "white",
                      color: "white",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.1)",
                      },
                    }}
                  >
                    Admin
                  </Button>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  startIcon={<LogoutIcon />}
                  onClick={handleLogout}
                  sx={{
                    borderColor: "white",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  Cerrar Sesión
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ mb: 3 }}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<LoginIcon />}
                onClick={() => setLoginOpen(true)}
                sx={{
                  borderColor: "white",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                Iniciar Sesión
              </Button>
            </Box>
          )}

          {/* Contact Buttons */}
          <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
            <Button
              variant="contained"
              fullWidth
              startIcon={<Phone />}
              href="tel:+56993015600"
              sx={{
                backgroundColor: "white",
                color: "primary.main",
                "&:hover": { backgroundColor: "#f5f5f5" },
              }}
            >
              Llamar
            </Button>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<WhatsApp />}
              href="https://wa.me/56993015600"
              target="_blank"
              sx={{
                borderColor: "white",
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              WhatsApp
            </Button>
          </Box>

          {/* Footer info */}
          <Box sx={{ mt: "auto", pt: 3, textAlign: "center" }}>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              +569 3197 2877
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              contacto@tattersall.cl
            </Typography>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

const useNavbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categoriesMenuAnchor, setCategoriesMenuAnchor] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  // 🆕 NUEVO: Hook para tracking de navegación
  const { markNavigationFromHome } = useNavigationMemory();

  // Obtener categorías únicas
  const categories = [...new Set(mockProducts.map((p) => p.category))];

  // Detectar scroll para cambiar estilo
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      setScrolled(isScrolled);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Menú items
  const menuItems = [
    { label: "Inicio", path: "/", icon: <Home /> },
    { label: "Catálogo", path: "/catalogo", icon: <Inventory /> },
    // { label: "Categorías", path: null, icon: <Category />, hasSubmenu: true },
    // { label: "Nosotros", path: "/nosotros", icon: <Info /> },
    { label: "Contacto", path: "/contacto", icon: <ContactPhone /> },
  ];

  const handleMenuClick = (path) => {
    if (path) {
      navigate(path);
    }
    setMobileMenuOpen(false);
  };

  const handleCategoriesClick = (event) => {
    setCategoriesMenuAnchor(event.currentTarget);
  };

  const handleCategoriesClose = () => {
    setCategoriesMenuAnchor(null);
  };

  // 🆕 FUNCIÓN MEJORADA: Manejar selección de categoría con tracking
  const handleCategorySelect = (category) => {
    if (category) {
      const filters = { category: category };

      // Marcar que la navegación viene desde Navbar con filtros
      markNavigationFromHome(filters);
    }

    // Navegar al catálogo con o sin categoría
    navigate(`/catalogo?category=${encodeURIComponent(category)}`);
    handleCategoriesClose();
    setMobileMenuOpen(false);
  };

  const isActiveRoute = (path) => {
    if (path === "/" && location.pathname === "/") return true;
    if (path !== "/" && location.pathname.startsWith(path)) return true;
    return false;
  };

  return {
    navigate,
    location,
    mobileMenuOpen,
    setMobileMenuOpen,
    categoriesMenuAnchor,
    setCategoriesMenuAnchor,
    scrolled,
    setScrolled,
    categories,
    menuItems,
    handleMenuClick,
    handleCategoriesClick,
    handleCategoriesClose,
    handleCategorySelect,
    isActiveRoute,
  };
};

const Navbar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { openLoginModal, getLoginModalState } = useApp();
  const { anonymousQuotesAssociated, clearAnonymousQuotesMessage } = useAuth();

  // Estados para los modales de registro y verificación
  const [registerOpen, setRegisterOpen] = useState(false);
  const [verifyCodeOpen, setVerifyCodeOpen] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  // 🆕 NUEVO: Escuchar eventos personalizados desde otros componentes (LoginModal, QuoteForm, etc.)
  useEffect(() => {
    const handleOpenLogin = () => {
      console.log("🔔 Evento recibido: openNavbarLogin");
      openLoginModal();
    };

    const handleOpenRegister = () => {
      console.log("🔔 Evento recibido: openNavbarRegister");
      setRegisterOpen(true);
    };

    // Agregar listeners
    window.addEventListener("openNavbarLogin", handleOpenLogin);
    window.addEventListener("openNavbarRegister", handleOpenRegister);

    // Cleanup
    return () => {
      window.removeEventListener("openNavbarLogin", handleOpenLogin);
      window.removeEventListener("openNavbarRegister", handleOpenRegister);
    };
  }, [openLoginModal]);

  // Handler para éxito en el registro
  const handleRegistrationSuccess = (data) => {
    console.log("✅ Usuario registrado exitosamente:", data.email);
    setRegisteredEmail(data.email);
    setRegisterOpen(false);
    setVerifyCodeOpen(true);
    setRegistrationSuccess(true);
  };

  // Handler para éxito en la verificación
  const handleVerificationSuccess = () => {
    console.log("✅ Email verificado exitosamente");
    setVerifyCodeOpen(false);
    setRegistrationSuccess(false);
    // Mostrar el modal de login para que el usuario inicie sesión
    openLoginModal();
  };

  // Handler para abrir registro desde el login
  const handleOpenRegister = () => {
    setRegisterOpen(true);
  };

  return (
    <>
      {isMobile ? (
        <MobileNavbar
          props={{
            setLoginOpen: openLoginModal,
            setRegisterOpen: setRegisterOpen,
          }}
        />
      ) : (
        <DesktopNavbar
          props={{
            setLoginOpen: openLoginModal,
            setRegisterOpen: setRegisterOpen,
          }}
        />
      )}

      {/* Spacer for fixed navbar */}
      <Toolbar />

      {/* Banner de notificación de horarios */}
      <NotificationBanner />

      {/* Notificación de cotizaciones asociadas automáticamente */}
      {anonymousQuotesAssociated?.success && (
        <Snackbar
          open={true}
          autoHideDuration={6000}
          onClose={clearAnonymousQuotesMessage}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          sx={{ mt: 8 }} // Offset para que no se superponga con el navbar
        >
          <Alert
            onClose={clearAnonymousQuotesMessage}
            severity="success"
            variant="filled"
            sx={{ width: "100%" }}
          >
            {anonymousQuotesAssociated.message}
          </Alert>
        </Snackbar>
      )}

      {/* Notificación de registro exitoso */}
      {registrationSuccess && (
        <Snackbar
          open={true}
          autoHideDuration={4000}
          onClose={() => setRegistrationSuccess(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          sx={{ mt: 8 }}
        >
          <Alert
            onClose={() => setRegistrationSuccess(false)}
            severity="info"
            variant="filled"
            sx={{ width: "100%" }}
          >
            ¡Registro exitoso! Revisa tu email para el código de verificación
          </Alert>
        </Snackbar>
      )}

      {/* Modal de Login */}
      <LoginForm
        open={getLoginModalState()}
        onClose={openLoginModal}
        onOpenRegister={handleOpenRegister}
      />

      {/* Modal de Registro */}
      <RegisterForm
        open={registerOpen}
        onClose={() => setRegisterOpen(false)}
        onSuccess={handleRegistrationSuccess}
        onOpenLogin={() => {
          setRegisterOpen(false);
          openLoginModal();
        }}
      />

      {/* Modal de Verificación de Código */}
      <VerifyCodeForm
        open={verifyCodeOpen}
        onClose={() => setVerifyCodeOpen(false)}
        email={registeredEmail}
        onVerificationSuccess={handleVerificationSuccess}
      />
    </>
  );
};

export default Navbar;
